import { createAsyncThunk } from '@reduxjs/toolkit';
import PaymentHooks from '../utils/PaymentHooks';
import {
  EFTPOS_STATUS_UPDATE,
  FAILURE_REASON,
  PAYMENT_METHOD,
} from '../constants';
import Logger from '../utils/Logger';
import i18next from 'i18next';
import { updateSelectedPaymentMethod } from '../reducers/currentOrder/selectedPaymentMethods';
import { sale } from './sale';
import getSelectedPaymentMethods from '../selectors/getSelectedPaymentMethods';
import getOrderUUID from '../selectors/getOrderUUID';
import { setReceiptLink } from '../reducers/currentOrder/receiptLink';

const ZELLER_PURCHASE_RESULT = 'zellerPurchaseResult';
const ZELLER_PURCHASE_ERROR = 'zellerPurchaseError';
const ZELLER_PURCHASE_EVENT = 'zellerPurchaseEvent';

type ZellerShellResponse = {
  requestId: string;
  response?: any;
  error?: {
    message?: string;
  };
  event?: {
    event?: string;
  };
};

const sendZellerMessageToShell = (type: string, message: unknown) => {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ type, message }));
};

const zellerPurchaseFromShell = ({
  amount,
  reference,
  statusCallback,
}: {
  amount: number;
  reference: string;
  statusCallback: (statusMsg: string, txnStarted: boolean) => void;
}) => {
  // React Native WebView cannot access the shell's Zeller terminal object
  // directly, so correlate the shell response back to this specific purchase.
  const requestId = `${reference || 'zeller'}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;

  let settled = false;
  let cleanup = () => {
    settled = true;
  };
  let rejectPurchase: (reason?: any) => void = () => {};

  const promise = new Promise<any>((resolve, reject) => {
    rejectPurchase = reject;
    cleanup = () => {
      settled = true;
      window.removeEventListener(
        ZELLER_PURCHASE_RESULT,
        onResult as EventListener,
      );
      window.removeEventListener(
        ZELLER_PURCHASE_ERROR,
        onError as EventListener,
      );
      window.removeEventListener(
        ZELLER_PURCHASE_EVENT,
        onEvent as EventListener,
      );
    };

    const onResult = (event: CustomEvent<ZellerShellResponse>) => {
      if (event.detail?.requestId !== requestId) {
        return;
      }

      cleanup();
      resolve(event.detail.response);
    };

    const onError = (event: CustomEvent<ZellerShellResponse>) => {
      if (event.detail?.requestId !== requestId) {
        return;
      }

      cleanup();
      reject(new Error(event.detail.error?.message || 'Zeller payment failed'));
    };

    const onEvent = (event: CustomEvent<ZellerShellResponse>) => {
      if (event.detail?.requestId !== requestId) {
        return;
      }

      const zellerEvent = event.detail.event;
      const enhancedStatus = i18next.t(
        `zellerNotification.${zellerEvent?.event}`,
      );

      // Mirror the direct SDK path so the existing EFTPOS modal keeps receiving
      // Zeller status updates while the shell owns the native SDK call.
      statusCallback(enhancedStatus, zellerEvent?.event === 'PROCESSING');
    };

    window.addEventListener(ZELLER_PURCHASE_RESULT, onResult as EventListener);
    window.addEventListener(ZELLER_PURCHASE_ERROR, onError as EventListener);
    window.addEventListener(ZELLER_PURCHASE_EVENT, onEvent as EventListener);

    sendZellerMessageToShell('zellerPurchase', {
      requestId,
      amount,
      reference,
    });
  });

  return {
    promise,
    cancel: () => {
      if (!settled) {
        sendZellerMessageToShell('zellerPurchaseCancel', { requestId });
        cleanup();
        rejectPurchase(new Error(FAILURE_REASON.PAYMENT_CANCELLED));
      }
    },
  };
};

export const zellerPayments = createAsyncThunk(
  '$zellerPayments',
  async (
    data: {
      amount: number;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    let reason: FAILURE_REASON | undefined;
    try {
      const { amount } = data;
      const orderId = getOrderUUID(getState() as EntireFrontendState);

      const hook: any = PaymentHooks.get(EFTPOS_STATUS_UPDATE);

      const statusCallback = (statusMsg: string, txnStarted: boolean) => {
        if (hook) {
          hook({ statusMsg: statusMsg, txnStarted: txnStarted });
        }
      };

      statusCallback(i18next.t('zellerNotification.STARTED'), false);
      const reference = orderId || '';
      // Android runs the kiosk UI inside a React Native WebView. The Zeller SDK
      // lives in the shell there, while Electron can still use window.parent.
      const useShellBridge = Boolean(window.ReactNativeWebView);
      let cancelPurchase: () => void;
      let promise: Promise<any>;

      if (useShellBridge) {
        const shellPurchase = zellerPurchaseFromShell({
          amount,
          reference,
          statusCallback,
        });

        promise = shellPurchase.promise;
        cancelPurchase = shellPurchase.cancel;
      } else {
        const terminal = window.parent._initZeller;

        if (!terminal?.purchase) {
          throw new Error('Zeller terminal is not initialised');
        }

        const terminalPromise = terminal.purchase({
          amount,
          reference,
          configuration: {
            unattended: true,
          },
        });

        cancelPurchase = () => terminalPromise.cancel?.();

        terminalPromise.onEvent?.((event: { event?: string }) => {
          const enhancedStatus = i18next.t(`zellerNotification.${event.event}`);

          let started = false;
          if (event.event === 'PROCESSING') {
            started = true;
          }
          statusCallback(enhancedStatus, started);
        });

        promise = terminalPromise;
      }

      PaymentHooks.subscribe('CANCEL_TRANSACTION', data => {
        if (data.cancelled) {
          Logger.log(`===> #### Cancel Transaction Clicked ### ${reason}`);
          cancelPurchase();
        }
      });

      const response = await promise;
      const isError = [
        'Network Failure',
        'Connection Failure',
        'Setup Required',
        'Feature Not Available',
        'Card Read Failure',
        'Card Insert Required',
        'Operation Failure',
        'Transaction Response Invalid',
        'Transaction Network Failure',
        'Transaction System Failure',
        'Transaction Failure',
      ].includes(response.type);
      const cardCancelled = [
        'Cancelled',
        'Card Blocked',
        'Card Not Supported',
        'Card Declined',
        'Transaction Declined',
      ].includes(response.type);
      const terminalBusy = [
        'Permissions Missing',
        'Operation Busy',
        'Operation Interrupted',
        'Operation Request Invalid',
        'Operation Response Invalid',
        'Paired Device Busy',
        'Inactivity Timeout',
        'Transaction Response Timeout',
        'Paired Device Not Available',
      ].includes(response.type);

      if (terminalBusy) {
        reason = FAILURE_REASON.PAYMENT_TIMED_OUT;
        throw new Error(reason);
      } else if (cardCancelled) {
        reason = FAILURE_REASON.PAYMENT_CANCELLED;
        throw new Error(reason);
      } else if (isError) {
        reason = FAILURE_REASON.PAYMENT_UNSUCCESSFUL;
        throw new Error(reason);
      }

      if (reason) {
        Logger.log(JSON.stringify(`Zeller status ${response.type}`, null, 2));
        statusCallback(response.type, true);
      }

      Logger.log(`-----  ----- ZELLER TRANSACTION  ----- ----`);
      Logger.log(JSON.stringify(response, null, 2));
      Logger.log(`----- ----- ----- -----  -----  -----  -----`);

      const subPayments = getSelectedPaymentMethods(
        getState() as EntireFrontendState,
      );
      if (response.$type === 'Approved' && response.type === 'PURCHASE') {
        const subPayment = subPayments.filter(
          subPayment => subPayment.method === PAYMENT_METHOD.EFTPOS,
        )[0];

        // The terminal approved the charge, but the EFTPOS entry is gone
        // from selectedPaymentMethods (e.g. navigation reset it
        // mid-transaction). Do not submit a sale with an empty Payments[] -
        // the card was charged, so surface it for staff.
        if (!subPayment) {
          reason = FAILURE_REASON.CHARGED;
          Logger.log(
            `===> #### ZELLER APPROVED BUT NO SELECTED PAYMENT ### ${reason}`,
          );
          throw new Error(reason);
        }

        const receiptLink = response?.receiptLink;
        const lineLength = 40;
        let splitReceipt = '';
        for (let i = 0; i < receiptLink.length; i += lineLength) {
          splitReceipt += receiptLink.slice(i, i + lineLength) + '\n';
        }
        const updatedSubPayment = {
          ...subPayment,
          receiptText: splitReceipt,
          // receiptLink: response?.receiptLink,
          cardType: response?.scheme,
          referenceNumber: response?.transactionUuid,
          // authorisationCode: response?.authCode,
        };
        dispatch(setReceiptLink(response?.receiptLink));
        dispatch(updateSelectedPaymentMethod(updatedSubPayment));
        dispatch(sale({ route: 'checkout', authenticationMethod: 'trusted' }));
      }
      if (response.$type === 'Declined' && response.type === 'PURCHASE') {
        reason = FAILURE_REASON.PAYMENT_DECLINED;
        throw new Error(reason);
      }
    } catch (e) {
      Logger.log(`Error ===> Zeller payment failed ${e}`);
      return rejectWithValue({
        e,
        reason: reason ? reason : (e as any).message,
      });
    }
  },
);
